/**
 * Org membership invite core — extracted VERBATIM from
 * `organization.router.ts`'s `invite` handler so the agency prospect flow
 * (`agency.inviteProspect`) can run the exact same battle-tested path:
 * stub-User creation for brand-new emails, the new-vs-resend decision
 * tree, the 7-day one-click magic-link token, bell notification, email
 * with copyable fallback link, and the audit log row.
 *
 * Same split as `deal-core.ts`: permission checks stay INSIDE the core
 * (callerRole/requireRole against the acting session), so a caller cannot
 * reach the write without passing the same authorization the router
 * enforced before the extraction. Behavior when called from
 * `organization.invite` is byte-for-byte what it was.
 *
 * @package @app/api
 */

import { ORPCError } from '@orpc/server';
import { writeAuditLog } from './audit';

export const ORG_ROLES = ['owner', 'admin', 'compliance', 'member'] as const;
export const STAFF_ROLES = ['owner', 'admin', 'compliance'] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

// biome-ignore lint/suspicious/noExplicitAny: prisma client type
type Db = any;

/**
 * Staff-role assignment side effect — sets a flag that tells the
 * /onboarding gate "this person is not (just) an athlete; skip the NCAA
 * ID forced flow on first login." They can still go to /profile and
 * enter their NCAA ID voluntarily; we just don't block them at the
 * door.
 *
 * Idempotent: if the user already has `onboardingNcaaIdSkippedAt`
 * (either because they manually skipped or because we set it before),
 * no-op.
 */
export async function autoSkipNcaaOnboardingForStaff(db: Db, userId: string): Promise<void> {
  await db.user.updateMany({
    where: { id: userId, onboardingNcaaIdSkippedAt: null },
    data: { onboardingNcaaIdSkippedAt: new Date() },
  });
}

/** Caller's role in a given org, or null if not a member. */
export async function callerRole(db: Db, userId: string, orgId: string): Promise<OrgRole | null> {
  const m = await db.member.findFirst({
    where: { userId, organizationId: orgId },
    select: { role: true },
  });
  return (m?.role as OrgRole | undefined) ?? null;
}

export function requireRole(role: OrgRole | null, allowed: readonly OrgRole[]): void {
  if (!role || !allowed.includes(role)) {
    throw new ORPCError('FORBIDDEN', {
      message: `Requires one of: ${allowed.join(', ')}`,
    });
  }
}

export interface InviteMemberResult {
  memberId: string;
  userId: string;
  role: OrgRole;
  isNewUser: boolean;
  isResend: boolean;
  emailSent: boolean;
  emailError: string | null;
  inviteUrl: string;
}

/**
 * Invite `email` into `orgId` as `role`, acting as `context.session.user`.
 * See module docblock — this IS the old `organization.invite` handler
 * body, verbatim, with `input.*` renamed to the parameters.
 */
export async function inviteMemberCore(
  // biome-ignore lint/suspicious/noExplicitAny: mirrors the oRPC handler context shape
  context: { db: any; session: any },
  { orgId, email, role }: { orgId: string; email: string; role: OrgRole },
): Promise<InviteMemberResult> {
    const { db, session } = context;
    const callerR = await callerRole(db, session.user.id, orgId);
    requireRole(callerR, ['owner', 'admin']);
    if (role === 'owner' && callerR !== 'owner') {
      throw new ORPCError('FORBIDDEN', { message: 'Only owners can promote other owners.' });
    }

    // Raw prisma — see organization.create comment for the ZenStack
    // bootstrap-policy reason. Authorization is enforced by callerRole
    // + requireRole above.
    const { prisma: rawPrisma, createId } = await import('@app/db');

    const targetEmail = email.trim().toLowerCase();
    let targetUser = await rawPrisma.user.findUnique({
      where: { email: targetEmail },
      select: { id: true, email: true, name: true, emailVerified: true },
    });
    const isNewUser = !targetUser;
    const isStaff = (STAFF_ROLES as readonly string[]).includes(role);
    // "New invite vs. resend" decision tree:
    //   no user row                       → create stub user + member
    //   user row + no member row          → add member (real existing user)
    //   user row + member + not verified  → RESEND (stale stub from a
    //                                       previous attempt that the
    //                                       invitee never finished —
    //                                       refresh the magic-link
    //                                       token + email)
    //   user row + member + verified      → CONFLICT (real existing
    //                                       member is already in the
    //                                       org and can't be re-added)
    let isResend = false;

    if (!targetUser) {
      const placeholderName = targetEmail.split('@')[0] || 'Invited Member';
      const newUserId = createId();
      await rawPrisma.user.create({
        data: {
          id: newUserId,
          email: targetEmail,
          name: placeholderName,
          // Staff suppresses the athlete onboarding gate (NCAA prompt
          // + missing-ID banner) immediately on first sign-in.
          userType: isStaff ? 'staff' : 'athlete',
          onboardingNcaaIdSkippedAt: isStaff ? new Date() : null,
        },
      });
      targetUser = { id: newUserId, email: targetEmail, name: placeholderName, emailVerified: false };
    } else {
      const existing = await rawPrisma.member.findFirst({
        where: { userId: targetUser.id, organizationId: orgId },
      });
      if (existing) {
        if (targetUser.emailVerified) {
          throw new ORPCError('CONFLICT', {
            message: `${targetEmail} is already a ${existing.role} of this org.`,
          });
        }
        // Stale stub from a prior invite that the invitee never
        // completed. Treat as a resend — update role if changed +
        // refresh the magic-link token below.
        isResend = true;
        if (existing.role !== role) {
          await rawPrisma.member.update({
            where: { id: existing.id },
            data: { role: role },
          });
        }
      }
    }

    const member = isResend
      ? // For resend: keep the existing member row so we don't violate
        // the @@unique([userId, organizationId]) constraint. Re-read so
        // we can return the id consistently.
        (await rawPrisma.member.findFirst({
          where: { userId: targetUser.id, organizationId: orgId },
        }))!
      : await rawPrisma.member.create({
          data: {
            id: createId(),
            userId: targetUser.id,
            organizationId: orgId,
            role: role,
          },
        });

    const org = await db.organization.findUnique({
      where: { id: orgId },
      select: { name: true },
    });
    const orgName = org?.name ?? 'an organization';
    if (isStaff) {
      await autoSkipNcaaOnboardingForStaff(rawPrisma, targetUser.id);
    }

    const roleLabel =
      role === 'owner'
        ? 'an owner'
        : role === 'admin'
          ? 'an admin'
          : role === 'compliance'
            ? 'a compliance officer'
            : 'a member';
    const dashboardUrl = isStaff ? '/compliance/submissions' : '/settings/organizations';
    const actionUrl = isNewUser ? '/auth/login' : dashboardUrl;

    // Generate a one-click magic-link URL embedded directly in the
    // invitation email so the invitee never has to retype their email.
    // Better-auth's /api/auth/magic-link/verify endpoint reads tokens
    // from the `verification` table (see
    // better-auth@1.6.11/dist/plugins/magic-link/index.mjs:59-77), so
    // inserting a row with the right shape gives us a token that
    // better-auth will recognize. The endpoint also calls
    // `findUserByEmail` + signs them in to my stub User row — verified
    // end-to-end against prod: 302 to callbackURL + Set-Cookie session.
    //
    // Tokens are valid 7 days (vs. the standard 5-min magic-link window)
    // since invitations realistically aren't clicked the moment they
    // arrive. Single-use per better-auth's `consumeVerificationValue`.
    const { randomBytes } = await import('node:crypto');
    const inviteToken = randomBytes(24).toString('base64url');
    const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    await rawPrisma.verification.create({
      data: {
        id: createId(),
        identifier: inviteToken,
        value: JSON.stringify({ email: targetUser.email, name: targetUser.name }),
        expiresAt,
      },
    });
    // Origin-aware: previews each have their own per-branch Neon DB
    // (Vercel-Neon Previews integration), so a magic link generated
    // on a preview must point back at THAT preview — not prod —
    // otherwise prod's better-auth can't find the stub User row.
    // Prefer the user-facing aliases (production / branch) over the
    // unique deployment hash so the invitee URL is stable across
    // redeploys.
    const baseUrl =
      (process.env.VERCEL_ENV === 'production' && process.env.VERCEL_PROJECT_PRODUCTION_URL
        ? `https://${process.env.VERCEL_PROJECT_PRODUCTION_URL}`
        : process.env.VERCEL_BRANCH_URL
          ? `https://${process.env.VERCEL_BRANCH_URL}`
          : process.env.VERCEL_URL
            ? `https://${process.env.VERCEL_URL}`
            : 'https://www.nilboard.app');
    const inviteUrl =
      baseUrl +
      '/auth/accept-invite' +
      '?token=' + encodeURIComponent(inviteToken) +
      '&callbackURL=' + encodeURIComponent(dashboardUrl);

    // In-app notification (only meaningful for existing users — new
    // users haven't signed in yet, but the row sits waiting for them).
    await rawPrisma.notification.create({
      data: {
        id: createId(),
        userId: targetUser.id,
        type: 'system',
        title: `You're now ${roleLabel} of ${orgName}`,
        body: `${session.user.name ?? session.user.email ?? 'A teammate'} added you to ${orgName} on NilBoard.${
          isStaff ? ' You can now access the compliance dashboards.' : ''
        }`,
        actionUrl,
      },
    });

    // Email — different copy for new vs. existing users.
    // `??` would let empty-string names through (Better-Auth has been
    // observed to seed `name = ""` for magic-link sign-ups when the
    // user hasn't filled in a profile name yet), producing emails that
    // start with a literal leading space. `.trim() || ...` collapses
    // both null/undefined AND whitespace-only.
    const inviterLabel =
      (session.user.name && session.user.name.trim()) ||
      (session.user.email && session.user.email.trim()) ||
      'A teammate';
    const subject = isNewUser
      ? `${inviterLabel} invited you to ${orgName} on NilBoard`
      : `You're now ${roleLabel} of ${orgName}`;
    const body = isNewUser
      ? `${inviterLabel} added you to ${orgName} on NilBoard as ${roleLabel}.\n\n` +
        `One-click sign in (expires in 7 days):\n${inviteUrl}\n\n` +
        `Or visit https://www.nilboard.app and sign in with ${targetUser.email} via magic link.${
          isStaff ? `\n\nOnce signed in, your compliance dashboards live at https://www.nilboard.app${dashboardUrl}.` : ''
        }`
      : `${inviterLabel} added you to ${orgName} on NilBoard.${
          isStaff ? ' You can now access the compliance dashboards.' : ''
        }\n\nOne-click sign in (expires in 7 days):\n${inviteUrl}\n\n` +
        `Or visit https://www.nilboard.app${dashboardUrl} directly.`;

    let emailSent = false;
    let emailError: string | null = null;
    try {
      const { sendNotification } = await import('@app/notifications');
      await sendNotification({
        userId: targetUser.id,
        email: targetUser.email,
        channels: ['email'],
        title: subject,
        body,
      });
      emailSent = true;
    } catch (e) {
      emailError = e instanceof Error ? e.message : String(e);
      console.error('[organization.invite] email notification failed:', emailError);
    }

    await writeAuditLog({
      action: isResend
        ? 'organization.member_invite_resent'
        : isNewUser
          ? 'organization.member_invited_new_user'
          : 'organization.member_added',
      category: 'compliance',
      actor: {
        id: session.user.id,
        name: session.user.name ?? null,
        email: session.user.email ?? null,
        role: session.user.role ?? null,
      },
      target: { type: 'user', id: targetUser.id, name: targetUser.email },
      context: { orgId: orgId, role: role, isNewUser, isResend, emailSent },
    });

    // Surface emailSent + the magic-link URL so the UI can show a
    // copyable fallback link if Resend failed (preview envs without
    // RESEND_API_KEY, rate limits, etc.).
    return {
      memberId: member.id,
      userId: targetUser.id,
      role: role,
      isNewUser,
      isResend,
      emailSent,
      emailError,
      inviteUrl,
    };
}
